import { useState } from 'react'
import { fetcher, AdminGameSeasonClawMachinePrizesEndpoints, IUseGameSeasonClawMachinePrizes } from '@olagg/api-hooks'
import { AdminSeasonClawMachinePrize } from '@olagg/db-types'

interface PrizesResponse {
  gameSeasonClawMachinePrizes: AdminSeasonClawMachinePrize[]
  meta: {
    count: number
  }
}

const useClawPrizes = () => {
  const [loading, setLoading] = useState(false)

  const getClawPrizes = async (filter: IUseGameSeasonClawMachinePrizes & { game_season_id: number }): Promise<PrizesResponse> => {
    try {
      setLoading(true)
      const endpoint = AdminGameSeasonClawMachinePrizesEndpoints.all(filter)
      const response = await fetcher(endpoint)
      return response
    } catch (error) {
      console.error('Error fetching claw machine prizes:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const getClawPrize = async (id: string, game_season_id: string): Promise<AdminSeasonClawMachinePrize> => {
    try {
      setLoading(true)
      const endpoint = AdminGameSeasonClawMachinePrizesEndpoints.find({ id, game_season_id })
      const response = await fetcher(endpoint)
      return response
    } catch (error) {
      console.error('Error fetching claw machine prize:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const createClawPrize = async (data: Partial<AdminSeasonClawMachinePrize> & { game_season_id: string }): Promise<AdminSeasonClawMachinePrize> => {
    try {
      setLoading(true)
      const { game_season_id, ...prizeData } = data
      const endpoint = AdminGameSeasonClawMachinePrizesEndpoints.create({ data: prizeData, game_season_id })
      const response = await fetcher(endpoint)
      return response
    } catch (error) {
      console.error('Error creating claw machine prize:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const updateClawPrize = async (id: string, data: Partial<AdminSeasonClawMachinePrize> & { game_season_id: string }): Promise<AdminSeasonClawMachinePrize> => {
    try {
      setLoading(true)
      const { game_season_id, ...prizeData } = data
      const endpoint = AdminGameSeasonClawMachinePrizesEndpoints.update({ id, data: prizeData, game_season_id })
      const response = await fetcher(endpoint)
      return response
    } catch (error) {
      console.error('Error updating claw machine prize:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const deleteClawPrize = async (id: string, game_season_id: string): Promise<void> => {
    try {
      setLoading(true)
      const endpoint = AdminGameSeasonClawMachinePrizesEndpoints.delete(id, game_season_id)
      await fetcher(endpoint)
    } catch (error) {
      console.error('Error deleting claw machine prize:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  return {
    loading,
    getClawPrizes,
    getClawPrize,
    createClawPrize,
    updateClawPrize,
    deleteClawPrize,
  }
}

export default useClawPrizes
